import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { FormikProps } from 'formik'
import { useDisconnect } from 'wagmi'

import { IBuyFormValues } from './components/buy-form/buy-form.controller'
import {
  BuyerActions,
  CampaignPublicActions,
  campaignPublicSelector,
  isLoadingCampaignPublicSelector,
  mediumCampaignPublicImageSelector,
  originalCampaignPublicImageSelector,
  walletAddressSelector,
} from 'primsell-shared-layer/features'

import { OrderActions, StripeActions } from 'features'

import { CodeTypeE } from 'primsell-shared-layer/types'
import { infoSelector, PaymentActions } from 'features'

const usePaymentController = () => {
  const [amount, setAmount] = useState<IBuyFormValues['amount']>('')
  const [isPaymentSessionExpired, setIsPaymentSessionExpired] = useState<boolean>(false)
  const [agreeCheck, setAgreeCheck] = useState<boolean>(true)

  const formikExternalRef = useRef<FormikProps<IBuyFormValues>>(null)

  const dispatch = useDispatch()
  const walletAddress = useSelector(walletAddressSelector)
  const publicCampaignDetails = useSelector(campaignPublicSelector)
  const isLoadingCampaignPublic = useSelector(isLoadingCampaignPublicSelector)
  const info = useSelector(infoSelector)
  const mediumPublicCampaignImage = useSelector(mediumCampaignPublicImageSelector)
  const originalPublicCampaignImage = useSelector(originalCampaignPublicImageSelector)

  const { code } = useParams<'code'>()

  const { disconnect } = useDisconnect({
    onSuccess() {
      dispatch(BuyerActions.clearBuyerData())
    },
  })

  const handleDisconnect = useCallback(() => {
    disconnect()
    dispatch(StripeActions.setClientSecret({ clientSecret: '' }))
    dispatch(StripeActions.setAccountId({ accountId: '' }))
    dispatch(OrderActions.createOrderResponse({ expPaymentDate: undefined }))
    formikExternalRef?.current?.resetForm()
    setAgreeCheck(false)
    dispatch(PaymentActions.clearPaymentInfo())
  }, [dispatch, disconnect])

  const handleRenewPaymentSession = useCallback(() => {
    setIsPaymentSessionExpired(false)
    code && dispatch(CampaignPublicActions.getPublicCampaignDetailsByCodeRequest({ code }))
  }, [code, dispatch])

  const totalPrice = useMemo(() => +(publicCampaignDetails?.price || 0) * +amount, [amount, publicCampaignDetails])

  const isConnectButtonDisabled: boolean = useMemo(() => {
    if (!publicCampaignDetails?.startDate || !publicCampaignDetails?.endDate) {
      return true
    }

    if (publicCampaignDetails?.status === 'completed') {
      return true
    }

    const startDate = new Date(publicCampaignDetails.startDate)
    const endDate = new Date(publicCampaignDetails.endDate)
    const currentDate = new Date()

    return startDate.getTime() > currentDate.getTime() || endDate.getTime() < currentDate.getTime()
  }, [publicCampaignDetails?.endDate, publicCampaignDetails?.startDate, publicCampaignDetails?.status])

  const handleConnectWalletSuccess = useCallback(
    (wallet: string) => {
      if (!publicCampaignDetails?.contractAddress || publicCampaignDetails.codeType !== CodeTypeE.LOYALTY) return

      dispatch(
        PaymentActions.checkBalanceAfterConnect({
          contractAddress: publicCampaignDetails.contractAddress,
          address: wallet,
        })
      )
    },
    [publicCampaignDetails, dispatch]
  )

  useEffect(() => {
    if (code) {
      dispatch(CampaignPublicActions.getPublicCampaignDetailsByCodeRequest({ code }))
    }
  }, [code, dispatch])

  useEffect(() => {
    if (
      publicCampaignDetails?.codeType === CodeTypeE.LOYALTY &&
      publicCampaignDetails?.maxAmountPerPurchase &&
      amount !== publicCampaignDetails.maxAmountPerPurchase.toString() &&
      walletAddress
    ) {
      setAmount(publicCampaignDetails.maxAmountPerPurchase.toString())
    } else if (!walletAddress) {
      setAmount('')
    }
  }, [amount, publicCampaignDetails?.codeType, publicCampaignDetails?.maxAmountPerPurchase, walletAddress])

  return {
    publicCampaignDetails,
    isLoadingCampaignPublic,
    info,
    mediumPublicCampaignImage,
    originalPublicCampaignImage,
    setIsPaymentSessionExpired,
    isPaymentSessionExpired,
    totalPrice,
    walletAddress,
    isConnectButtonDisabled,
    handleDisconnect,
    amount,
    setAmount,
    formikExternalRef,
    handleRenewPaymentSession,
    agreeCheck,
    setAgreeCheck,
    handleConnectWalletSuccess,
  }
}

export { usePaymentController }
